import type {
  GeocodingService,
  GeocodeResult,
  ReverseGeocodeResult,
} from './types';

export class MapboxGeocodingService implements GeocodingService {
  private accessToken: string | null = null;

  constructor() {
    this.initialize();
  }

  private initialize() {
    const config = window.runtimeConfig || {};
    this.accessToken = config.MAPBOX_KEY || null;
  }

  isConfigured(): boolean {
    return this.accessToken !== null && this.accessToken !== 'undefined';
  }

  async geocodeAddress(address: string): Promise<GeocodeResult | null> {
    if (!this.accessToken) {
      throw new Error('Mapbox access token not configured');
    }

    if (!address || address.trim() === '') {
      throw new Error('Address is required');
    }

    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${this.accessToken}`
    );

    if (!response.ok) {
      throw new Error(`Mapbox geocoding failed: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.features && data.features.length > 0) {
      const feature = data.features[0];
      const [lng, lat] = feature.center;

      return {
        address,
        coordinates: { lat, lng },
        formattedAddress: feature.place_name,
      };
    }

    return null;
  }

  async reverseGeocode(
    lat: number,
    lng: number
  ): Promise<ReverseGeocodeResult | null> {
    if (!this.accessToken) {
      throw new Error('Mapbox access token not configured');
    }

    if (isNaN(lat) || isNaN(lng)) {
      throw new Error('Valid latitude and longitude are required');
    }

    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${this.accessToken}`
    );

    if (!response.ok) {
      throw new Error(
        `Mapbox reverse geocoding failed: ${response.statusText}`
      );
    }

    const data = await response.json();

    if (data.features && data.features.length > 0) {
      return {
        address: data.features[0].place_name,
        formattedAddress: data.features[0].place_name,
      };
    }

    return null;
  }
}
